import React, { useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "./ui";
import { Sparkles, X } from "lucide-react";
import { translate as t, tp } from "../i18n";
import { useApp } from "../context/AppContext";

// Re-trigger a specific app's Quick Tour on demand (used by the "?" replay buttons).
export function replayTour(appKey) {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("fopfo:replay-tour", { detail: { appKey } }));
  }
}

// A one-time, dismissible coachmark. On the first visit to an app it dims the page,
// rings the target control (by data-testid) and shows a short tooltip. Dismissal is
// remembered per app in localStorage so it never nags again.
// Bump TOUR_VERSION to make the one-time welcome coachmarks re-appear once for everyone.
const TOUR_VERSION = "v2";

export default function QuickTour({ appKey, targetTestId, title, lines = [] }) {
  const { lang } = useApp();
  const storeKey = `tour_seen_${TOUR_VERSION}_${appKey}`;
  const [show, setShow] = useState(false);
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try { if (localStorage.getItem(storeKey)) return; } catch (e) { return; }
    let timer;
    const reveal = () => { timer = setTimeout(() => setShow(true), 800); }; // let the page paint first
    if (window.__cookieConsent != null) {
      reveal();
      return () => clearTimeout(timer);
    }
    // Hold the guided tour until the cookie banner has been resolved, so its buttons aren't blocked.
    const onConsent = () => { window.removeEventListener("cookie-consent", onConsent); reveal(); };
    window.addEventListener("cookie-consent", onConsent);
    return () => { window.removeEventListener("cookie-consent", onConsent); clearTimeout(timer); };
  }, [storeKey]);

  // Allow the tour to be re-triggered on demand (e.g. a "?" button) via a window event.
  useEffect(() => {
    const onReplay = (e) => {
      if (e?.detail?.appKey && e.detail.appKey !== appKey) return;
      try { localStorage.removeItem(storeKey); } catch (err) { /* ignore */ }
      setShow(true);
    };
    window.addEventListener("fopfo:replay-tour", onReplay);
    return () => window.removeEventListener("fopfo:replay-tour", onReplay);
  }, [appKey, storeKey]);

  useLayoutEffect(() => {
    if (!show) return;
    const measure = () => {
      const el = document.querySelector(`[data-testid="${targetTestId}"]`);
      setRect(el ? el.getBoundingClientRect() : null);
    };
    measure();
    const id = setInterval(measure, 500); // re-measure while layout settles
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      clearInterval(id);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [show, targetTestId]);

  const dismiss = () => {
    try { localStorage.setItem(storeKey, "1"); } catch (e) { /* ignore */ }
    setShow(false);
  };

  if (!show) return null;

  const tipW = 330;

  return createPortal(
    <div className="fixed inset-0 z-[80] pointer-events-none" data-testid={`quick-tour-${appKey}`}>
      <div className="absolute inset-0 bg-black/55 pointer-events-none" />
      {rect && (
        <div
          className="absolute rounded-lg ring-4 ring-[var(--primary)] pointer-events-none"
          style={{ top: rect.top - 4, left: rect.left - 4, width: rect.width + 8, height: rect.height + 8, boxShadow: "0 0 0 9999px rgba(0,0,0,0.0)" }}
        />
      )}
      <div
        className="fixed rounded-xl bg-white dark:bg-[#1c1c1c] text-[#161616] dark:text-white shadow-2xl p-4 space-y-2 pointer-events-auto"
        style={{ right: 24, bottom: 24, width: tipW, maxWidth: "calc(100vw - 32px)" }}
        data-testid={`quick-tour-tip-${appKey}`}
      >
        <div className="flex items-center gap-2">
          <Sparkles size={16} className="text-[var(--primary)]" />
          <span className="font-head text-base">{title}</span>
          <button onClick={dismiss} className="ms-auto opacity-60 hover:opacity-100" data-testid={`quick-tour-close-${appKey}`}><X size={16} /></button>
        </div>
        {lines.map((l, i) => <p key={i} className="text-sm opacity-80">{l}</p>)}
        <div className="flex justify-end pt-1">
          <Button onClick={dismiss} data-testid={`quick-tour-gotit-${appKey}`}>{tp("Got it", lang)}</Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
